/**
 * Cart Notification Component
 * Shows a notification when items are added to cart
 */

class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.timeout = null;
  }

  connectedCallback() {
    // Create notification container if it doesn't exist
    if (!document.getElementById('cart-notification-container')) {
      const container = document.createElement('div');
      container.id = 'cart-notification-container';
      container.setAttribute('aria-live', 'polite');
      container.setAttribute('aria-atomic', 'true');
      document.body.appendChild(container);
    }
  }

  show(productData, addons = []) {
    const container = document.getElementById('cart-notification-container');
    if (!container) {
      this.connectedCallback();
    }

    // Check if there's an existing notification
    const existingNotification = container.querySelector('.cart-notification');
    if (existingNotification && existingNotification.classList.contains('cart-notification--show')) {
      // Update existing notification with addons if provided
      if (addons && addons.length > 0) {
        const addonsContainer = existingNotification.querySelector('.cart-notification__addons');
        if (!addonsContainer) {
          const textContainer = existingNotification.querySelector('.cart-notification__text');
          if (textContainer) {
            const addonsHTML = this.renderAddons(addons);
            const linkElement = textContainer.querySelector('.cart-notification__link');
            if (linkElement && addonsHTML) {
              linkElement.insertAdjacentHTML('beforebegin', addonsHTML);
            }
          }
        } else if (addons.length > 0) {
          // Update existing addons
          const addonsHTML = this.renderAddons(addons);
          if (addonsHTML) {
            addonsContainer.outerHTML = addonsHTML;
          }
        }
        return; // Don't create a new notification, just update
      }
    }

    // Remove existing notification if any
    if (existingNotification) {
      existingNotification.remove();
    }

    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'cart-notification';
    notification.setAttribute('role', 'alert');

    // Get product image
    const productImage = productData.image || this.getProductImage() || '';
    const productName = productData.name || this.getProductName() || 'Item';
    
    // Build notification HTML
    let notificationHTML = `
      <div class="cart-notification__content">
        <div class="cart-notification__icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="9" fill="currentColor"/>
            <path d="M6 10L9 13L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="cart-notification__text">
          <p class="cart-notification__message">Item added to your cart</p>
          <p class="cart-notification__product-name">${this.escapeHtml(productName)}</p>
          ${addons.length > 0 ? this.renderAddons(addons) : ''}
          <a href="${window.routes?.cart_url || '/cart'}" class="cart-notification__link">View cart</a>
        </div>
        <button class="cart-notification__close" aria-label="Close notification" type="button">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
    `;

    notification.innerHTML = notificationHTML;
    container.appendChild(notification);

    // Trigger animation
    requestAnimationFrame(() => {
      notification.classList.add('cart-notification--show');
    });

    // Auto-hide after 5 seconds
    this.timeout = setTimeout(() => {
      this.hide(notification);
    }, 5000);

    // Close button handler
    const closeBtn = notification.querySelector('.cart-notification__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.hide(notification);
      });
    }

    // Update cart count after a delay to ensure all items are added
    // If addons are present, wait a bit longer
    const delay = addons && addons.length > 0 ? 800 : 300;
    setTimeout(() => {
      this.updateCartCount();
    }, delay);
  }

  renderAddons(addons) {
    if (!addons || addons.length === 0) return '';
    
    const addonList = addons.map(addon => {
      const addonName = addon.productTitle || addon.variantTitle || 'Addon';
      return `<li class="cart-notification__addon">${this.escapeHtml(addonName)}</li>`;
    }).join('');

    return `
      <div class="cart-notification__addons">
        <p class="cart-notification__addons-title">Add-ons:</p>
        <ul class="cart-notification__addons-list">
          ${addonList}
        </ul>
      </div>
    `;
  }

  hide(notification) {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (notification) {
      notification.classList.remove('cart-notification--show');
      notification.classList.add('cart-notification--hide');
      
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    }
  }

  getProductName() {
    // Try to get product name from various sources
    const productTitle = document.querySelector('.product__title, h1.product__title');
    if (productTitle) {
      return productTitle.textContent.trim();
    }

    const productForm = document.querySelector('product-form');
    if (productForm) {
      const productSection = productForm.closest('[data-section]');
      if (productSection) {
        const title = productSection.querySelector('.product__title, h1');
        if (title) {
          return title.textContent.trim();
        }
      }
    }

    return 'Product';
  }

  getProductImage() {
    // Try to get product image
    const productImage = document.querySelector('.product__media img, .product__media-wrapper img');
    if (productImage) {
      return productImage.src;
    }

    return null;
  }

  async updateCartCount() {
    // Fetch updated cart count
    try {
      // Add a small delay to ensure cart is fully updated on server
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`);
      if (response.ok) {
        const cart = await response.json();
        const count = cart.item_count || 0;
        
        // Update cart bubble count
        const cartBubbleCount = document.querySelector('.header-cart__bubble-count');
        if (cartBubbleCount) {
          if (count < 100) {
            cartBubbleCount.textContent = count;
          } else {
            cartBubbleCount.textContent = '99+';
          }
          
          // Update bubble size classes
          const bubble = cartBubbleCount.closest('.header-cart__bubble');
          if (bubble) {
            bubble.classList.remove('header-cart__bubble--small', 'header-cart__bubble--medium', 'header-cart__bubble--large');
            if (count < 10) {
              bubble.classList.add('header-cart__bubble--small');
            } else if (count < 100) {
              bubble.classList.add('header-cart__bubble--medium');
            } else {
              bubble.classList.add('header-cart__bubble--large');
            }
          }
        }

        // Also update via cart-icon-bubble section if available
        if (window.routes?.cart_url) {
          fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
            .then(res => res.text())
            .then(text => {
              const html = new DOMParser().parseFromString(text, 'text/html');
              const cartIconSection = html.querySelector('#cart-icon-bubble, [data-section-type="cart-icon-bubble"]');
              if (cartIconSection) {
                const cartIconEl = document.getElementById('cart-icon-bubble');
                if (cartIconEl) {
                  cartIconEl.innerHTML = cartIconSection.innerHTML;
                }
              }
            })
            .catch(err => console.error('Error updating cart icon:', err));
        }
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Make it globally available
if (!customElements.get('cart-notification')) {
  customElements.define('cart-notification', CartNotification);
}

// Global function to show notification
window.showCartNotification = function(productData, addons) {
  let notification = document.querySelector('cart-notification');
  if (!notification) {
    notification = document.createElement('cart-notification');
    document.body.appendChild(notification);
  }
  notification.show(productData, addons);
};

